import { useEffect, useState } from "react";
import { BadgeCheck, Clock, CheckCircle2, Award, Download } from "lucide-react";
import AppShell from "../AppShell";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { exportCertificatePDF } from "../pdf";
import PremiumCertificate from "../../components/site/PremiumCertificate";
import ScaledCertificate from "../../components/certificate/ScaledCertificate";
import {
  PageHeader,
  Button,
  ErrorBanner,
  EmptyState,
} from "../../components/primitives";

export default function Training() {
  const { business } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [error, setError] = useState("");
  const [showCert, setShowCert] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const [a, c] = await Promise.all([api.get("/assignments"), api.get("/certificates")]);
      setAssignments(a);
      setCertificates(c);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function complete(id) {
    setBusyId(id);
    try {
      const res = await api.post(`/assignments/${id}/complete`);
      await load();
      if (res.certificate) setShowCert(res.certificate);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const pending = assignments.filter((a) => a.status !== "completed");
  const completed = assignments.filter((a) => a.status === "completed");

  return (
    <AppShell>
      <PageHeader
        testid="training-page"
        kicker="Training & certificates"
        title="Prove your team is informed"
        description="Each acknowledgement is signed, time stamped and minted into a verifiable certificate you can download."
      />

      <ErrorBanner error={error} className="mb-5" />

      <section className="mb-10">
        <SectionHeader icon={Clock} title="Awaiting acknowledgement" count={pending.length} tone="amber" />
        <div className="mt-4 space-y-3" data-testid="training-pending-list">
          {pending.length === 0 && (
            <EmptyState
              icon={CheckCircle2}
              title="Nothing outstanding"
              description="Your team is up to date on every assigned SOP and policy."
            />
          )}
          {pending.map((a) => (
            <div
              key={a.id}
              data-testid={`training-row-${a.id}`}
              className="rounded-xl border border-slate-200 bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 text-[14.5px] font-semibold tracking-tight truncate">
                  {a.team_member_name}
                  <span className="text-slate-400 font-normal"> · {a.team_member_role}</span>
                </div>
                <div className="mt-1 text-[13px] text-slate-500 truncate">
                  {a.document_title} <span className="text-slate-400">{a.document_version}</span>
                </div>
                <div className="mt-1.5 text-[11px] text-slate-500 uppercase tracking-[0.14em]">
                  Assigned {new Date(a.assigned_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
              <Button
                variant="accent"
                icon={CheckCircle2}
                onClick={() => complete(a.id)}
                disabled={busyId === a.id}
                data-testid={`training-complete-${a.id}`}
              >
                {busyId === a.id ? "Signing…" : "Mark acknowledged"}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <SectionHeader icon={Award} title="Signed certificates" count={certificates.length} tone="emerald" />
        <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="training-certs-list">
          {certificates.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">
              No certificates yet. Complete an assignment above to mint the first one.
            </div>
          )}
          {certificates.map((c) => (
            <div
              key={c.cert_id}
              data-testid={`training-cert-${c.cert_id}`}
              className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3"
            >
              <span className="h-10 w-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                <BadgeCheck size={16} className="text-emerald-600" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 text-[14px] font-semibold tracking-tight truncate">
                  {c.team_member_name}
                </div>
                <div className="text-[12.5px] text-slate-500 truncate">{c.module} {c.version}</div>
                <div className="text-[11px] font-mono text-slate-500 mt-1 break-all">{c.cert_id}</div>
              </div>
              <button
                onClick={() => setShowCert(c)}
                data-testid={`training-cert-view-${c.cert_id}`}
                className="shrink-0 text-[12px] font-semibold text-[#0B1220] hover:text-[#8C6B33] border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-1.5"
              >
                View
              </button>
            </div>
          ))}
        </div>
      </section>

      {showCert && <CertificateModal cert={showCert} business={business} onClose={() => setShowCert(null)} />}
    </AppShell>
  );
}

function SectionHeader({ icon: Icon, title, count, tone }) {
  const toneClass = tone === "emerald" ? "text-emerald-700" : "text-amber-700";
  return (
    <div className="flex items-center justify-between">
      <h2 className={`inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.16em] font-semibold ${toneClass}`}>
        <Icon size={14} />
        {title}
      </h2>
      <span className="text-[11px] text-slate-500 font-mono">{count}</span>
    </div>
  );
}

function CertificateModal({ cert, business, onClose }) {
  const [downloading, setDownloading] = useState(false);

  const certProps = {
    staffName: cert.team_member_name,
    staffRole: cert.team_member_role || "",
    organisation: business?.name || cert.business_name || "",
    competencyTitle: cert.module,
    sopReferences: cert.version ? [`${cert.module} ${cert.version}`] : [],
    score:
      typeof cert.score === "number"
        ? `${cert.score}%`
        : cert.score || "Acknowledged",
    passMark: "66%",
    completedOn: cert.completed_at
      ? new Date(cert.completed_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "",
    // Certificates are stored without valid_until (the API returns it empty),
    // so every issued certificate printed a dash for the expiry. Derive it from
    // the issue date instead - 12 months, matching the dossier's default
    // certificate_validity_months.
    validUntil: (() => {
      const src = cert.valid_until || cert.completed_at;
      if (!src) return "";
      const d = new Date(src);
      if (Number.isNaN(d.getTime())) return "";
      if (!cert.valid_until) d.setMonth(d.getMonth() + 12);
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    })(),
    certId: cert.cert_id,
  };

  async function download() {
    setDownloading(true);
    try {
      const safeName = (cert.team_member_name || "certificate")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      await exportCertificatePDF(certProps, `${safeName}-${cert.cert_id}.pdf`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-start justify-center p-3 sm:p-5 overflow-y-auto" onClick={onClose}>
      <div className="max-w-4xl w-full my-4 sm:my-6" onClick={(e) => e.stopPropagation()} data-testid="training-cert-modal">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-white text-sm">Certificate preview</div>
          <div className="flex items-center gap-2">
            <button
              onClick={download}
              disabled={downloading}
              data-testid="training-cert-print"
              className="inline-flex items-center gap-1.5 bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] rounded-lg px-3 py-1.5 text-[12px] font-semibold"
            >
              <Download size={12} /> {downloading ? "Building PDF…" : "Download PDF"}
            </button>
            <button onClick={onClose} className="text-white/80 hover:text-white text-[12px]">Close</button>
          </div>
        </div>
        {/* Mobile: horizontal scroll keeps the A4 layout legible at true
            proportions rather than shrinking it to ~358px wide. */}
        <div className="rounded-xl bg-white shadow-2xl overflow-x-auto">
          <div className="min-w-[720px]">
            <ScaledCertificate {...certProps} />
          </div>
        </div>
      </div>
    </div>
  );
}
